import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

export const SelectionContext = createContext();

// Debounced localStorage save
const useDebouncedSave = () => {
  const timeoutRef = useRef(null);
  
  return useCallback((key, value) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error(`Failed to save ${key} to localStorage`, error);
      }
    }, 300); // 300ms debounce
  }, []);
};

export const SelectionProvider = ({ children }) => {
  const debouncedSave = useDebouncedSave();
  
  // Initialize selections from localStorage if available
  const [selections, setSelections] = useState(() => {
    try {
      const localData = localStorage.getItem('selections');
      return localData ? JSON.parse(localData) : {};
    } catch (error) {
      console.error("Failed to load selections from localStorage", error);
      return {};
    }
  });

  // Debounce localStorage saves
  useEffect(() => {
    debouncedSave('selections', selections);
  }, [selections, debouncedSave]);

  // Batch update function for multiple selections
  const batchUpdateSelections = useCallback((updates) => {
    setSelections(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Single selection update
  const updateSelection = useCallback((category, value) => {
    setSelections(prev => {
      // Only update if value actually changed
      if (prev[category] === value) return prev;
      
      return {
        ...prev,
        [category]: value
      };
    });
  }, []);

  // Clear selection
  const clearSelection = useCallback((category) => {
    setSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[category];
      return newSelections;
    });
  }, []);

  // Clear all selections
  const clearAllSelections = useCallback(() => {
    setSelections({});
    localStorage.removeItem('selections');
  }, []);

  // Get selection value with memoization
  const getSelection = useCallback((category) => {
    return selections[category];
  }, [selections]);

  // Check if category has selection
  const hasSelection = useCallback((category) => {
    return selections.hasOwnProperty(category) && selections[category] !== null && selections[category] !== '';
  }, [selections]);

  // Get selection count
  const selectionCount = useMemo(() => {
    return Object.keys(selections).filter(key => 
      selections[key] !== null && selections[key] !== ''
    ).length;
  }, [selections]);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    selections,
    setSelections,
    updateSelection,
    batchUpdateSelections,
    clearSelection,
    clearAllSelections,
    getSelection,
    hasSelection,
    selectionCount
  }), [
    selections, 
    updateSelection, 
    batchUpdateSelections, 
    clearSelection, 
    clearAllSelections,
    getSelection,
    hasSelection,
    selectionCount
  ]);

  return (
    <SelectionContext.Provider value={contextValue}>
      {children}
    </SelectionContext.Provider>
  );
};